import React, { useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CommonUtility } from "../../../../Services/CommonUtility";
import { RootState } from "../../../../shared/store";
import { updateSelectedLOBDetails, updateSelectedPolicyDetails } from "../../../customer-slice/Customer-slice";
import { PolicyAdditionalInterestDto } from "../../../model/PolicyAdditionalInterestDto";
import DataTable, { ITableColumn } from "../../../../components/components/Table/DataTable";
import CustomerAdditionalInterestsForm from "./CustomerAdditionalInterestsForm";
import CustomerAdditionalInterestsTableConfig from "./CustomerAdditionalInterestsTableConfig";
import { v4 as uuid } from "uuid";
import * as constant from '../../../../shared/util/constant';
import { Button } from "../../../../components";
import { CustomerPolicyDetailLabels as cstPolicyAdditionalInterestsLabels } from '../../../../shared/util/label';
import { useConfirmationModalContext } from "../../../../shared/components/ModalConfirmationContext";
import { CustomerPolicyDetails as cstPolicyDetails } from '../../../../shared/util/constant';
import './CustomerAdditionalInterests.scss';

export interface ICustomerPolicyAdditionalInterestsList {
    tabIndex?: number;
}

const CustomerAdditionalInterestsList: React.FunctionComponent<ICustomerPolicyAdditionalInterestsList> = (props) => {
    let tbIndex = props.tabIndex;
    // Get policyId and polEventId from Redux
    const { policyId, polEventId } = useSelector((state: RootState) => ({
        policyId: state.customerSlice.policyId,
        polEventId: state.customerSlice.polEventId
    }));
    const dispatch = useDispatch();
    const policyDetailKey = CommonUtility.getPolicyDetailKey(policyId, polEventId);
    const customerpolicyDetail = useSelector((state: RootState) => state?.customerSlice);
    const policyDetails = customerpolicyDetail.selectedPolicyDetails?.[policyDetailKey];
    const lob = customerpolicyDetail.selectedLOBDetails;
    const additionalInterests: PolicyAdditionalInterestDto[] = lob?.policyAdditionalInterests || [];
    const attachTypeLookup = useSelector((state: RootState) => state?.LookupState.attachTypeList) || [];
    const interestTypeLookup = useSelector((state: RootState) => state?.LookupState.interestTypeList) || [];
    const additionalInterestsWithAttachTypeValue = useMemo(() => {
        return additionalInterests.map(ai => ({
            ...ai,
            attachTypeValue: attachTypeLookup.find(opt => opt.value === ai.attachTypeId)?.label,
            interestTypeValue: interestTypeLookup.find(opt => opt.value === ai.interestTypeId)?.label
        }));
    }, [
        additionalInterests,
        attachTypeLookup,
        interestTypeLookup
    ]);
    const selectedLocationDetails = customerpolicyDetail.selectedLocationDetails;
    const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
    const [currentInterest, setCurrentInterest] = useState<PolicyAdditionalInterestDto | null>(null);
    const [isFormSaved, setIsFormSaved] = useState(true);
    const [addEditState, setAddEditState] = useState<{ isEditing: boolean; isAddingNew: boolean }>({ isEditing: false, isAddingNew: false });
    const additionalInterestsConfig = new CustomerAdditionalInterestsTableConfig();
    const modalContext = useConfirmationModalContext();

    // Handler for add
    const onAddNewInterest = () => {
        if (addEditState.isAddingNew || addEditState.isEditing) return;
        const newInterest: PolicyAdditionalInterestDto = {
            id: uuid(),
            polId: policyId,
            lobId: lob?.id || "",
            name1: "",
            name2: "",
            address1: "",
            address2: "",
            city: "",
            state: "",
            zip: "",
            isPayor: false,
            referenceNumber: "",
            attachTypeId: selectedLocationDetails ? constant.CS2_AttachTypes.LOCATION : constant.CS2_AttachTypes.LINE_OF_BUSINESS,
            attachId: selectedLocationDetails ? selectedLocationDetails.id : constant.DEFAULT_GUID ,
            effdate: new Date()
        };
        const updated = [newInterest, ...additionalInterests];
        updateLOB(updated);
        setCurrentInterest(newInterest);
        setExpandedRowId(newInterest.id);
        setAddEditState({ isEditing: false, isAddingNew: true });
        setIsFormSaved(false);
    };

    // Handler for delete
    const onRowDeleteClick = async (rowData: PolicyAdditionalInterestDto) => {
        const result = await modalContext.showConfirmation(cstPolicyDetails.DELETE_ADDITIONAL_INTEREST);
        if (!result) return;

        const updated = additionalInterests.filter((row) => row.id !== rowData.id);
        updateLOB(updated);
        setExpandedRowId(null);
        setCurrentInterest(null);
        setAddEditState({ isEditing: false, isAddingNew: false }); // <-- Enable Add button
        setIsFormSaved(true);
    };

    // Handler for row expand/collapse
    const onRowClick = (rowData: PolicyAdditionalInterestDto) => {
        if (addEditState.isEditing && expandedRowId !== rowData.id) return;
        if (expandedRowId === rowData.id) {
            setExpandedRowId(null);
            setCurrentInterest(null);
            setAddEditState({ isEditing: false, isAddingNew: false });
        } else {
            setExpandedRowId(rowData.id);
            setCurrentInterest(rowData);
            setAddEditState({ isEditing: true, isAddingNew: false });
        }
    };

    const onChevronClose = () => {
        setExpandedRowId(null);
        setCurrentInterest(null);
        setAddEditState({ isEditing: false, isAddingNew: false });
        setIsFormSaved(true);
    };

    const onEntityFieldChange = (propName: string, value: string | boolean) => {
        if (!currentInterest) return;
        let updatedInterest = { ...currentInterest, [propName]: value };
        if (propName === 'attachTypeId') {
            if (value === constant.CS2_AttachTypes.LOCATION) {
                updatedInterest.attachId = selectedLocationDetails ? selectedLocationDetails.id : constant.DEFAULT_GUID ;
            } else if (value === constant.CS2_AttachTypes.LINE_OF_BUSINESS) {
                updatedInterest.attachId = lob?.id || constant.DEFAULT_GUID ;
            } else {
                updatedInterest.attachId = constant.DEFAULT_GUID ;
            }
        }

        const updated = additionalInterests.map(ai => ai.id === updatedInterest.id ? updatedInterest : ai);
        updateLOB(updated);
        setCurrentInterest(updatedInterest);
        setIsFormSaved(false);
    };

    const onEntityFieldsChange = (updatedFields: Partial<PolicyAdditionalInterestDto>) => {
        if (!currentInterest) return;

        let updatedInterest: PolicyAdditionalInterestDto = {
            ...currentInterest,
            ...updatedFields
        };

        const updated = additionalInterests.map(ai => ai.id === updatedInterest.id ? updatedInterest : ai);
        updateLOB(updated);
        setCurrentInterest(updatedInterest);
        setIsFormSaved(false);
    };

    // Update LOB in Redux
    const updateLOB = (updatedInterests: PolicyAdditionalInterestDto[]) => {
        const updatedLOB = { ...lob, policyAdditionalInterests: updatedInterests };
        const updatedLOBs = (policyDetails.policyLineOfBusinesses || []).map(l =>
            l.id === updatedLOB.id ? updatedLOB : l
        );
        dispatch(updateSelectedPolicyDetails({
            policyId,
            polEventId,
            policyDetails: { ...policyDetails, policyLineOfBusinesses: updatedLOBs }
        }));
        dispatch(updateSelectedLOBDetails(updatedLOB));

    };


    let AddNewInterestButton = (
        <Button
            className="mb-2"
            onClick={onAddNewInterest}
            disabled={addEditState.isAddingNew || addEditState.isEditing}
            id="btnAdditionalInterestAdd"
            tabIndex={tbIndex++}
        >
            <i className="bi bi-plus-circle me-2"></i>
            {cstPolicyAdditionalInterestsLabels.ADD_ADDITIONAL_INTEREST}
        </Button>
    );

        // DataTable columns
    const columns: ITableColumn[] = additionalInterestsConfig.getAdditionalInterestsTableColumns(
        onRowClick,
        onChevronClose,
        onRowDeleteClick,
        tbIndex
    );



    return (
        <div className="customer-additional-interests-table">
            <div className="additional-interests-actions">
                {AddNewInterestButton}
            </div>
          <div className="additionalInterest-datatable-container"  >
            <DataTable                            
                columns={columns}
                data={additionalInterestsWithAttachTypeValue}
                keyColumnName="id"
                expandedRowId={expandedRowId}
                setExpandedRowId={setExpandedRowId}
                renderRow={row => (
                    (expandedRowId === row.id && currentInterest) ? (
                        <tr key={row.id}>
                            <td colSpan={columns.length}>
                                <div className="customer-additional-interests-form-wrapper">
                                    <CustomerAdditionalInterestsForm
                                        interest={currentInterest}
                                        onEntityFieldChange={onEntityFieldChange}
                                        onEntityFieldsChange={onEntityFieldsChange}
                                        tabIndex={tbIndex}
                                    />
                                </div>
                            </td>
                        </tr>
                    ) : null
                )}
                isFormSaved={isFormSaved}
                tableClassName="table-striped table-hover table-sm"
                theadClassName="tblHeader"
            />
            </div>
       
        </div>
    );
};

export default CustomerAdditionalInterestsList;
